import { Component, inject } from '@angular/core';
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, Subscription, skip } from 'rxjs';
import { Bundle, Patient, ServiceRequest, Specimen } from 'fhir/r4';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../shared/auth/auth.service';
import { EncounterServiceService } from '../patient-wrapper/encounter-service.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { StateService } from '../shared/state.service';
import { baseStatusStyles } from '../shared/statusUIIcons';
import { ErrorService } from '../shared/error.service';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AsyncPipe, CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import { JsonPipe } from '@angular/common';
import { References2Pipe } from '../shared/references2.pipe';
import { CodeableConcept2Pipe } from '../shared/codeable-concept2.pipe';
import { fetchFromReferencePipe } from '../shared/Fetch.pipe';
import { ChipsDirective } from '../chips.directive';
import { ReferenceDisplayDirective } from '../shared/reference-display.directive';
import { NaPipe } from '../shared/na.pipe';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { AddSpecimenComponent } from '../specimen/add-specimen/add-specimen.component';
import { SpecimenDetailsComponent } from '../specimen/specimen-details/specimen-details.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FhirResourceTransformService } from '../shared/fhir-resource-transform.service';
import { FhirResourceService } from '../shared/fhir-resource.service';
import { SuccessMessageComponent } from '../shared/success-message/success-message.component';
import { Router } from '@angular/router';
import { DetailsBuilderObject } from '../detailz-viewz/details-builder.service';
import { DetailActionButton, DetailzViewzComponent } from '../detailz-viewz/detailz-viewz.component';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TableHeaderComponent } from '../table-header/table-header.component';
import { UtilityService } from '../shared/utility.service';
import { combineDateAndTimeToIso } from '../shared/date-time-utils';
import { SpecimenContainerLabelPrintService } from '../shared/specimen-container-label-print.service';
import { ResourceStatusChangesService } from '../resource-status-changes/resource-status-changes.service';
import { DynamicFormsV2Component } from '../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { formMetaData, SingleCodeField } from '../shared/dynamic-forms.interface2';
import { ConfirmDialogComponent } from '../shared/confirm-dialog/confirm-dialog.component';
import { ReferenceDisplayService } from '../shared/reference-display.service';
import { ReferenceIdentifierPipe } from '../shared/reference-identifier.pipe';
import { SpecimenSearchService } from './specimen-search.service';
import { MatPaginatorModule } from '@angular/material/paginator';


@Component({
  selector: 'app-specimens',
  imports: [
    MatCardModule,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    MatTableModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    AsyncPipe,
    CommonModule,
    DatePipe,
    TitleCasePipe,
    JsonPipe,
    References2Pipe,
    CodeableConcept2Pipe,
    fetchFromReferencePipe,
    ChipsDirective,
    ReferenceDisplayDirective,
    NaPipe,
    EmptyStateComponent,
    TableHeaderComponent,
    ReferenceIdentifierPipe,
    MatPaginatorModule
  ],
  templateUrl: './specimens.component.html',
  styleUrls: ['../medicine-requests/medicine-requests.component.scss', './specimens.component.scss']
})
export class SpecimensComponent {
  specimenTableFilterArray = new Map<string, string[]>(
    [
      ['status', ['received', 'available', 'unavailable', 'cancelled']],
      ['type', ['Blood', 'Urine', 'Swab', 'Biopsy']]
    ]
  );
  specimenFiltersFormControlObject: Record<string, FormGroup> = {};
  private fb = inject(FormBuilder);
  private auth = inject(AuthService);
  encounterService = inject(EncounterServiceService);
  private stateService = inject(StateService);
  private errorService = inject(ErrorService);
  private router = inject(Router);
  dialog = inject(MatDialog);
  searchService = inject(SpecimenSearchService);
  statusStyles = baseStatusStyles;

  canAdd$ = new BehaviorSubject<boolean>(false);
  canExport$ = new BehaviorSubject<boolean>(false);

  specimenDisplayedColumns: string[] = ['identifier', 'receivedTime', 'status', 'type', 'subject', 'request', 'action'];

  patientId: string | null = null;
  private subscriptions = new Subscription();
  private useOrgWideSpecimens = false;
  searchableFields = [
    { label: 'Identifier', value: 'identifier' },
    { label: 'Patient ID', value: 'subject:Patient.identifier' }
  ];
  selectedSearchField: string | null = 'identifier';

  detailsBuilder: DetailsBuilderObject = {
    resourceName: 'Specimen',
    resourceIcon: 'science',
    specialHeader: {
      strongSectionKey: 'type',
      iconSectionKeys: ['status'],
      contentSectionKeys: ['subject', 'receivedTime']
    },
    groups: [
      { groupName: 'Identification', groupIcon: 'fingerprint', groupKeys: ['type', 'status', 'request', 'systemReference'] },
      { groupName: 'Collection', groupIcon: 'precision_manufacturing', groupKeys: ['collection.collector', 'bodySite', 'subject'] },
      { groupName: 'Logistics', groupIcon: 'calendar_today', groupKeys: ['receivedTime', 'collection.collectedDateTime'] },
      { groupName: 'Notes', groupIcon: 'note', groupKeys: ['note'] }
    ]
  };
  ngOnInit(): void {
    this.computePermissions('specimen')
    this.specimenTableFilterArray.forEach((values, key) => {
      const group = this.fb.group({});
      values.forEach(value => group.addControl(value, new FormControl(false)));
      this.specimenFiltersFormControlObject[key] = group;
    });
    const resolvedPatientId = this.stateService.currentPatientIdFromResolver.getValue();
    const encounterPatientId = this.stateService.currentEncounter.getValue()?.patientId ?? null;
    const canViewAllSpecimens = this.auth.can('specimen', 'viewAll');

    if (!resolvedPatientId && !encounterPatientId) {
      if (!canViewAllSpecimens) {
        this.errorService.openandCloseError('No patient selected. Please pick a patient to view specimens.');
        this.router.navigate(['/app/patients']);
        return;
      }
      this.useOrgWideSpecimens = true;
      this.patientId = null;
    } else {
      this.patientId = resolvedPatientId ?? encounterPatientId;
    }

    this.searchService.init(this.useOrgWideSpecimens ? null : this.patientId);

    const refreshSub = this.stateService.orgWideResources.specimens.pipe(skip(1)).subscribe(() => {
      try { this.searchService.refresh(); } catch (e) { }
    });
    this.subscriptions.add(refreshSub);

    this.computePermissions('specimen');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.searchService.destroy();
  }

  onFilterChanged(event: any): void {
    const filters: any = {};
    filters[event.filterKey] = event.values.join(',');
    this.searchService.search(filters);
  }

  onSearchCriteriaChanged(event: any): void {
    const filters: any = {};
    const field = event.field || this.selectedSearchField;
    let term = event.term;

    if (field === 'subject:Patient.identifier' && typeof term === 'string') {
        term = term.toUpperCase();
    }

    if (term && field) {
      filters[field] = term;
    }

    this.searchService.search(filters);
  }

  private snackBar = inject(MatSnackBar);
  private authService = inject(AuthService);
  private fhirTransformService = inject(FhirResourceTransformService);
  private fhirResourceService = inject(FhirResourceService);
  utilityService = inject(UtilityService);
  private specimenLabelPrintService = inject(SpecimenContainerLabelPrintService);
  private statusChanges = inject(ResourceStatusChangesService);
  private refDisplay = inject(ReferenceDisplayService);

  private readonly allowedSpecimenStatuses: Array<NonNullable<Specimen['status']>> = [
    'available',
    'unavailable',
    'unsatisfactory',
    'entered-in-error'
  ];

  private parseReferenceId(reference?: string | null, expectedType?: string): string | null {
    if (!reference || typeof reference !== 'string') return null;
    const [type, id] = reference.split('/');
    if (!type || !id) return null;
    if (expectedType && type !== expectedType) return null;
    return id;
  }

  private extractIdFromReference(ref?: string | null, expectedType?: string): string | null {
    const raw = (ref ?? '').toString().trim();
    if (!raw) return null;
    const noFrag = raw.split('#')[0].split('?')[0];
    const parts = noFrag.split('/').filter(Boolean);
    if (!parts.length) return null;
    if (expectedType) {
      const idx = parts.lastIndexOf(expectedType);
      if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
      return null;
    }
    return parts[parts.length - 1] || null;
  }

  private formatIsoDateTime(iso?: string | null): string | undefined {
    if (!iso) return undefined;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  }

  private findPatientInState(patientId: string | null): Patient | null {
    if (!patientId) return null;
    const current = this.stateService.PatientResources.currentPatient.getValue()?.actualResource as Patient | undefined;
    if (current?.id === patientId) return current;

    const orgWide = this.stateService.orgWideResources.patient.getValue() || [];
    return (orgWide.find(p => p.actualResource?.id === patientId)?.actualResource as Patient) ?? null;
  }

  private findServiceRequestInState(serviceRequestId: string | null): ServiceRequest | null {
    if (!serviceRequestId) return null;

    const fromPatient = this.stateService.PatientResources.serviceRequests.getValue() || [];
    const foundInPatient = fromPatient
      .map(e => e.actualResource as any)
      .find(r => r?.resourceType === 'ServiceRequest' && r?.id === serviceRequestId) as ServiceRequest | undefined;
    if (foundInPatient) return foundInPatient;

    const fromOrgWide = this.stateService.orgWideResources.serviceRequests.getValue() || [];
    return (fromOrgWide
      .map(e => e.actualResource as any)
      .find(r => r?.resourceType === 'ServiceRequest' && r?.id === serviceRequestId) as ServiceRequest | undefined) ?? null;
  }

  private getPatientPrimaryHealthId(patient: Patient | null, fallbackPatientId?: string | null): { primary: string; fallback?: string } {
    const identifiers = patient?.identifier || [];
    const hospitalIdIdentifier = identifiers.find(id =>
      id?.type?.coding?.some(c => c?.code === 'patient-hos-id') && !!id?.value
    ) || identifiers.find(id => id?.type?.coding?.some(c => c?.code === 'patient-hos-id'));

    const healthId = hospitalIdIdentifier?.value?.toString() || '';
    const patientResourceId = patient?.id?.toString() || (fallbackPatientId ?? '')?.toString();

    if (healthId) {
      return { primary: healthId, fallback: patientResourceId || undefined };
    }

    return { primary: patientResourceId || 'N/A' };
  }

  private getServiceRequestDisplay(sr: ServiceRequest | null): string | undefined {
    if (!sr) return undefined;
    const text = (sr.code as any)?.text;
    if (typeof text === 'string' && text.trim()) return text;
    const coding = (sr.code as any)?.coding?.[0];
    const display = coding?.display;
    if (typeof display === 'string' && display.trim()) return display;
    const code = coding?.code;
    if (typeof code === 'string' && code.trim()) return code;
    return undefined;
  }

  private codeableConceptToText(cc: any): string {
    if (!cc) return '';
    if (typeof cc === 'string') return cc;
    const coding = Array.isArray(cc?.coding) ? cc.coding : [];
    return (cc?.text ?? coding?.[0]?.display ?? coding?.[0]?.code ?? '').toString();
  }

  private extractContainerId(specimen: Specimen | any): string {
    const s: any = specimen as any;

    const ident = s?.container?.[0]?.identifier;
    if (ident) {
      if (Array.isArray(ident)) {
        const firstWithValue = ident.find((x: any) => x?.value) ?? ident[0];
        const v = firstWithValue?.value;
        if (v != null && String(v).trim()) return String(v).trim();
      } else {
        const v = ident?.value;
        if (v != null && String(v).trim()) return String(v).trim();
      }
    }

    const alt1 = s?.container?.[0]?.identifier?.[0]?.value;
    if (alt1 != null && String(alt1).trim()) return String(alt1).trim();

    const alt2 = s?.identifier?.[0]?.value ?? s?.identifier?.value;
    if (alt2 != null && String(alt2).trim()) return String(alt2).trim();

    return '';
  }

  private getPatientDisplayName(patient: Patient | null): string | undefined {
    if (!patient) return undefined;
    const names: any[] = Array.isArray((patient as any)?.name) ? (patient as any).name : [];
    const n = names[0] ?? null;
    const text = (n?.text ?? '').toString().trim();
    if (text) return text;
    const given = Array.isArray(n?.given) ? n.given.filter(Boolean).join(' ') : (n?.given ?? '').toString();
    const family = (n?.family ?? '').toString();
    const combined = `${given} ${family}`.trim();
    return combined || undefined;
  }
  private normalizeSpecimenStatus(status: unknown): Specimen['status'] {
    if (typeof status !== 'string') return undefined;

    const s = status.trim().toLowerCase();
    switch (s) {
      case 'available':
      case 'unavailable':
      case 'unsatisfactory':
      case 'entered-in-error':
        return s as Specimen['status'];
      default:
        return undefined;
    }
  }
  // private errorService = inject(ErrorService);
  // private stateService = inject(StateService);
  onAddSpecimen(patientId?: string, serviceRequestId?: string) {
    const dialogRef = this.dialog.open(AddSpecimenComponent, {
      maxHeight: '90vh',
      maxWidth: '650px',
      autoFocus: false,
      data: { patientId, serviceRequestId }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result || !result.values || (Array.isArray(result.values) && result.values.length === 0)) {
        this.errorService.openandCloseError('No Specimen was created as the form was closed without submission.');
        return;
      }

      let values = result.values;
      if (!Array.isArray(values)) {
        values = [values];
      }

      const practitionerId = this.authService.user.getValue()?.['fhirResourceId'];
      const practitionerRef = practitionerId ? `Practitioner/${practitionerId}` : null;

      const usedContainerIdentifierValues = new Set<string>();

      const specimenResources = values.map((val: any) => {
        // Unique container identifier per specimen (client-generated)
        let containerIdentifier = this.utilityService.generateLocalIdentifier({
          system: 'urn:elikita:specimen-container',
          prefix: 'container'
        });
        while (usedContainerIdentifierValues.has((containerIdentifier.value ?? '').toString())) {
          containerIdentifier = this.utilityService.generateLocalIdentifier({
            system: 'urn:elikita:specimen-container',
            prefix: 'container'
          });
        }
        usedContainerIdentifierValues.add((containerIdentifier.value ?? '').toString());

        // status., type, subject, request, receivedTime, ondition, bodySite, note
        const resource: Specimen = {
          resourceType: 'Specimen',
          status: this.normalizeSpecimenStatus(val.status),
          type: this.utilityService.toCodeableConcept(val.type),
          subject: this.fhirTransformService.toReference(val.subject),
          request: [this.fhirTransformService.toReference(val.request)],
          container: [
            {
              identifier: containerIdentifier
            } as any
          ],
          ...(val.receivedTime ? {
            receivedTime: combineDateAndTimeToIso(val.receivedTime, val.receivedTimeTime) ?? val.receivedTime,

          } : {}),
          ...(val.condition ? { condition: (Array.isArray(val.condition) ? val.condition : [val.condition]).filter((e: any) => e !== "" && String(e).trim() !== "").map((c: any) => this.fhirTransformService.toCodeableConcept(c)) } : {}),
          //note?
          ...(val.note ? { note: Array.isArray(val.note) ? val.note.map((n: any) => ({ text: n })) : [{ text: val.note }] } : {})
        }
        if (practitionerRef) {
          resource.collection = {
            ...(resource.collection || {}),
            collector: { reference: practitionerRef }
          };
        }

        if (val.bodySite) {
          resource.collection = {
            ...(resource.collection || {}),
            bodySite: this.utilityService.toCodeableConcept(val.bodySite)
          };
        }

        // resource.status = resource.status?.toLowerCase();/
        //  keys: ['isCollectedOverPeriod', 'collectionStart', 'collectionEnd', 'collectionDateTime'],
        if (val.collectedTime && val.collectedTime.isCollectedOverPeriod && (val.collectedTime?.collectionDateTime || (val.collectedTime?.collectionStart && val.collectedTime?.collectionEnd))) {
          resource.collection = {
            ...(resource.collection || {}),
            ...(String(val.collectedTime.isCollectedOverPeriod).toLowerCase() === 'no' && val.collectedTime.collectionDateTime ? {
              collectedDateTime: combineDateAndTimeToIso(val.collectedTime.collectionDateTime, val.collectedTime.collectionTime)
            } : {}),
            ...(String(val.collectedTime.isCollectedOverPeriod).toLowerCase() === 'yes' && val.collectedTime.collectionStart && val.collectedTime.collectionEnd ? {
              collectedPeriod: {
                start: combineDateAndTimeToIso(val.collectedTime.collectionStart, val.collectedTime.collectionStartTime),
                end: combineDateAndTimeToIso(val.collectedTime.collectionEnd, val.collectedTime.collectionEndTime)
              }
            } : {})
          };
        }
        return resource;
      });

      const bundle: Bundle<any> = {
        resourceType: 'Bundle',
        type: 'transaction',
        entry: specimenResources.map((resource: any) => ({
          resource,
          request: { method: 'POST', url: 'Specimen' }
        }))
      };

      this.fhirResourceService.postBundle(bundle).subscribe({
        next: (response) => {
          const persistedResources =
            response?.bundle?.entry?.map((entry: any) => entry.resource).filter(Boolean) ?? specimenResources;

          persistedResources.forEach((resource: any) => {
            this.stateService.persistOrgWideResource(resource, 'saved');
            if (patientId) {
              this.stateService.persistPatientResource(resource, 'saved');
            }
          });

          this.snackBar.openFromComponent(SuccessMessageComponent, {
            data: { message: 'Specimen record(s) created successfully.' },
            duration: 3000
          });
        },
        error: () => {
          this.errorService.openandCloseError('Error creating specimen record(s). Please try again later.');
        }
      });
    });
  }




  // this.encounterService.addSpecimen(this.patientId || undefined);

  showRow(specimen: Specimen) {
    const actionButtons: DetailActionButton[] = [
      {
        key: 'changeStatus',
        label: 'Change Status',
        icon: 'sync',
        color: 'warn',
        capabilities: [{ resource: 'specimen', action: 'edit' }]
      },
      {
        key: 'printSpecimenContainerLabel',
        label: 'Print specimen container label',
        icon: 'print',
        capabilities: [{ resource: 'specimen', action: 'viewAll' }]
      }
    ];

    const dialogRef = this.dialog.open(DetailzViewzComponent, {
      maxHeight: '93vh',
      maxWidth: '90vh',
      data: {
        resourceData: { ...specimen, systemReference: 'Specimen/' + specimen.id },
        detailsBuilderObject: this.detailsBuilder,
        actionButtons
      }
    });

    const actionSub = dialogRef.componentInstance.actionInvoked.subscribe(({ key, resource }) => {
      if (key === 'changeStatus') {
        this.promptSpecimenStatusChange((resource || specimen) as any as Specimen, dialogRef);
        return;
      }
      if (key !== 'printSpecimenContainerLabel') return;
      const res = (resource || specimen) as any as Specimen;

      const patientRef = (res?.subject as any)?.reference ? String((res?.subject as any).reference) : '';
      const serviceRequestRef = (res?.request?.[0] as any)?.reference ? String((res?.request?.[0] as any).reference) : '';

      const patientId = this.extractIdFromReference(patientRef, 'Patient');
      const serviceRequestId = this.extractIdFromReference(serviceRequestRef, 'ServiceRequest');

      const receivedTime = this.formatIsoDateTime(res?.receivedTime)
        || this.formatIsoDateTime((res as any)?.collection?.collectedDateTime)
        || this.formatIsoDateTime((res as any)?.collection?.collectedPeriod?.start);

      const containerId = this.extractContainerId(res as any);
      const specimenType = this.codeableConceptToText((res as any)?.type);

      const patientFromState = this.findPatientInState(patientId);
      const serviceRequestFromState = this.findServiceRequestInState(serviceRequestId);

      const patient$ = patientFromState
        ? of(patientFromState)
        : (patientId
          ? this.utilityService.threeTierQueryAndSyncToStateOnce$<Patient>({
            resourceType: 'Patient',
            scope: 'orgWide',
            count: 10,
            urlSearchObject: { _id: patientId },
            querySearchObject: { key: 'id', value: patientId },
            expectedMinLength: 1
          }).pipe(
            map((list) => (Array.isArray(list) ? (list[0] as Patient) : null)),
            catchError(() => of(null as unknown as Patient))
          )
          : of(null as unknown as Patient));

      const serviceRequest$ = serviceRequestFromState
        ? of(serviceRequestFromState)
        : (serviceRequestId
          ? this.utilityService.threeTierQueryAndSyncToStateOnce$<ServiceRequest>({
            resourceType: 'ServiceRequest',
            scope: patientId ? 'patient' : 'orgWide',
            patientId: patientId || null,
            patientScopeSearchParam: 'subject',
            count: 50,
            urlSearchObject: { _id: serviceRequestId },
            querySearchObject: [{ key: 'id', value: serviceRequestId }],
            expectedMinLength: 1
          }).pipe(
            map((list) => (Array.isArray(list) ? (list[0] as ServiceRequest) : null)),
            catchError(() => of(null as unknown as ServiceRequest))
          )
          : of(null as unknown as ServiceRequest));

      const patientName$ = patientRef
        ? this.refDisplay.ensure(patientRef, { displayPresent: (res?.subject as any)?.display || null }).pipe(
          catchError(() => of(''))
        )
        : of('');

      const serviceRequestName$ = serviceRequestRef
        ? this.refDisplay.ensure(serviceRequestRef, { displayPresent: (res?.request?.[0] as any)?.display || null }).pipe(
          catchError(() => of(''))
        )
        : of('');

      forkJoin({ patient: patient$, sr: serviceRequest$, patientName: patientName$, serviceRequestName: serviceRequestName$ })
        .subscribe(({ patient, patientName, serviceRequestName }) => {
          const ids = this.getPatientPrimaryHealthId(patient || null, patientId);
          const primaryPatientId = (patientId ?? '').toString().trim() || ids.primary || 'N/A';
          const fallbackId = ids.primary && ids.primary !== primaryPatientId ? ids.primary : (ids.fallback && ids.fallback !== primaryPatientId ? ids.fallback : undefined);

          this.specimenLabelPrintService.printSmallLabel({
            // IMPORTANT: Patient ID line should be the actual patientId (not name).
            patientPrimaryId: primaryPatientId,
            patientName: (patientName || '').toString().trim() || undefined,
            patientFallbackId: fallbackId,
            serviceRequestId: serviceRequestId || undefined,
            serviceRequestName: (serviceRequestName || '').toString().trim() || undefined,
            specimenType: specimenType || undefined,
            receivedTime,
            containerId: res.id || undefined
          });
        });
    });

    dialogRef.afterClosed().subscribe(() => actionSub.unsubscribe());
  }

  private promptSpecimenStatusChange(row: Specimen, detailsDialogRef?: any): void {
    const specimenId = (row as any)?.id?.toString?.() ?? '';
    if (!specimenId) {
      this.errorService.openandCloseError('Cannot change status: specimen has no id.');
      return;
    }

    if (!this.auth.can('specimen', 'edit')) {
      this.errorService.openandCloseError('You do not have permission to edit specimens.');
      return;
    }

    const currentStatus = ((row as any)?.status ?? 'unknown').toString().toLowerCase();
    const allowedStatuses = this.allowedSpecimenStatuses.filter(s => s !== currentStatus);
    if (!allowedStatuses.length) {
      this.errorService.openandCloseError('No alternative statuses available.');
      return;
    }

    const dynF = this.dialog.open(DynamicFormsV2Component, {
      width: '420px',
      data: {
        formMetaData: <formMetaData>{
          formName: 'Change Specimen Status',
          closeDialogOnSubmit: true,
          formDescription: `Current status: ${currentStatus}`,
          submitText: 'Update Status'
        },
        formFields: [
          <SingleCodeField>{
            generalProperties: <any>{
              fieldApiName: 'status',
              fieldLabel: 'Status',
              fieldName: 'Status',
              fieldType: 'SingleCodeField',
              inputType: 'select',
              isArray: false,
              isGroup: false,
              value: allowedStatuses[0],
              validations: [{
                type: 'default',
                isFunction: false,
                name: 'required'
              }]
            },
            data: allowedStatuses,
            dataType: 'string'
          }
        ]
      }
    });

    dynF.afterClosed().subscribe((formResult) => {
      const nextStatus = (formResult?.values?.status ?? '').toString().toLowerCase();
      if (!nextStatus) return;
      if (nextStatus === currentStatus) return;
      if (!allowedStatuses.includes(nextStatus as any)) {
        this.errorService.openandCloseError('Invalid status selection.');
        return;
      }

      const doUpdate = () => {
        const persistMode = this.useOrgWideSpecimens ? 'org' : 'patient';
        this.statusChanges.changeResourceStatus('Specimen', specimenId, nextStatus, persistMode as any).subscribe({
          next: ({ response }) => {
            const saved = (response?.resourceType === 'Specimen' ? response : null) as any as Specimen | null;
            if (saved && detailsDialogRef?.componentInstance) {
              detailsDialogRef.componentInstance.resourceData = saved as any;
              if (typeof detailsDialogRef.componentInstance.refresh === 'function') {
                detailsDialogRef.componentInstance.refresh();
              }
            }
            this.snackBar.open('Specimen status updated.', 'Dismiss', { duration: 2500 });
          },
          error: (err) => {
            console.error('Failed to change specimen status', err);
            this.errorService.openandCloseError('Failed to update specimen status.');
          }
        });
      };

      if (nextStatus === 'entered-in-error') {
        const ref = this.dialog.open(ConfirmDialogComponent, {
          maxWidth: '520px',
          data: {
            title: 'Mark specimen as entered-in-error?',
            message: 'This should be used only when the specimen was created by mistake. Continue?',
            confirmText: 'Mark entered-in-error',
            cancelText: 'Cancel',
            icon: 'warning'
          }
        });
        ref.afterClosed().subscribe((confirmed: boolean) => {
          if (confirmed === true) doUpdate();
        });
        return;
      }

      doUpdate();
    });
  }


  showSpecimenDetails(specimen: Specimen): void {
    this.dialog.open(SpecimenDetailsComponent, {
      maxWidth: '450px',
      maxHeight: '90vh',
      data: { specimen }
    });
  }

  onExportSpecimens(): void {
    console.warn('Export specimens not implemented yet.');
  }

  /**
   * Compute and set permissions for a given resource type
   * @param resourceType - The resource type to check permissions for
   */
  public computePermissions(resourceType: string): void {
    this.canAdd$.next(this.auth.can(resourceType as any, 'add'));
    this.canExport$.next(this.auth.can(resourceType as any, 'viewAll'));
  }
}
